/**
 * Category Hero Slideshow
 * Displays categories in the homepage hero section
 */

import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MapPinIcon, StarIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { categoryService, Category } from '../../services/categoryService';
import { tourService } from '../../services/tourService';
import { useCategoryOrder } from '../../contexts/CategoryOrderContext';
import { useTheme } from '../../contexts/ThemeContext';
import { applyFeaturedOrderWithCategory } from '../../utils/applyFeaturedOrder';
import { getCategorySubtitle } from '../../utils/categoryTitles';
import { 
  getHeroImageForCategory, 
  getOptimizedHeroUrl,
  HERO_IMAGE_WIDTH,
  HERO_IMAGE_QUALITY
} from '../../utils/heroImageUtils';
import { generateImageSrcSet, getOptimalImageWidth } from '../../utils/optimizeImageUrl';
import CachedImage from '../Common/CachedImage';


const DEFAULT_HERO_IMAGE = "https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2072&q=80";

const DEFAULT_CATEGORY: Category = {
  id: 'default-hero',
  name: 'SightTrail Safaris',
  description: 'World-Class African Wilderness. Experience the majesty of the Great Migration and witness Africa\'s Big Five with our professional safari architects.',
  isActive: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString()
};

const CategoryHeroSlideshow: React.FC = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [categoryImages, setCategoryImages] = useState<Record<string, string>>({});
  const [optimalWidth, setOptimalWidth] = useState(2560);
  const { featuredCategories: categories, loading } = useCategoryOrder();
  const { actualTheme } = useTheme();
  const categoriesRef = useRef<string>('');

  // Calculate optimal image width
  useEffect(() => {
    const updateOptimalWidth = () => {
      setOptimalWidth(getOptimalImageWidth());
    };
    updateOptimalWidth();
    window.addEventListener('resize', updateOptimalWidth);
    return () => window.removeEventListener('resize', updateOptimalWidth);
  }, []);

  // Load category images
  useEffect(() => {
    if (categories.length > 0) {
      const categoryIds = categories.map(c => c.id).join(',');
      if (categoryIds !== categoriesRef.current) {
        categoriesRef.current = categoryIds;
        loadCategoryImages(categories);
      }
    }
  }, [categories]);

  const loadCategoryImages = async (categories: Category[]) => {
    const images: Record<string, string> = {};
    try {
      const allTours = await tourService.getAllTours(false);

      for (const category of categories) {
        const heroUrl = getHeroImageForCategory(category, allTours);
        if (heroUrl) {
          images[category.id] = heroUrl;
        }
      }

      setCategoryImages(images);
    } catch (error) {
      console.error('Error loading hero category images:', error);
    }
  };

  const getCategoryImage = (category: Category): string => {
    if (category.id === DEFAULT_CATEGORY.id) return DEFAULT_HERO_IMAGE;
    return categoryImages[category.id] || '';
  };

  // Determine what to display: Real categories if loaded, otherwise Default Fallback
  // This removes the "loading spinner" delay entirely
  const displayCategories = categories.length > 0 ? categories : [DEFAULT_CATEGORY];

  // Auto-advance slides
  useEffect(() => {
    if (displayCategories.length > 1) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % displayCategories.length);
      }, 5000);

      return () => clearInterval(timer);
    }
  }, [displayCategories.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % displayCategories.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + displayCategories.length) % displayCategories.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  const currentCategory = displayCategories[currentSlide] || DEFAULT_CATEGORY;
  const isDefault = currentCategory.id === DEFAULT_CATEGORY.id;

  return (
    <section className={`hero-section relative min-h-[calc(100vh-5rem)] flex items-center justify-center bg-white dark:bg-slate-900 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${actualTheme === 'dark' ? 'bg-slate-900' : 'bg-white'
      }`}>
      <div className="relative w-full max-w-7xl mx-auto min-h-[calc(100vh-7rem)] rounded-2xl md:rounded-[2rem] overflow-hidden flex items-center justify-center floating-card shadow-2xl border-2 border-white/20 dark:border-slate-700/30">
        {/* Slideshow Images */}
        <div className="absolute inset-0 z-0">
          {displayCategories.map((category, index) => (
            <div
              key={category.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${index === currentSlide ? 'opacity-100' : 'opacity-0'
                }`}
            >
              {getCategoryImage(category) ? (
                <CachedImage
                  src={getOptimizedHeroUrl(getCategoryImage(category))}
                  alt={category.name}
                  className="w-full h-full object-cover"
                  style={{
                    filter: 'brightness(1.05) contrast(1.15) saturate(1.2)',
                    imageRendering: 'crisp-edges',
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                    objectFit: 'cover',
                    objectPosition: 'center'
                  } as React.CSSProperties}
                  loading="eager" // Force eager loading for instant Hero
                  decoding="sync" // Decode synchronously for faster paint
                  // @ts-ignore
                  fetchPriority="high" // Prioritize this resource
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className={`w-full h-full transition-colors duration-300 ${actualTheme === 'dark' ? 'bg-slate-950' : 'bg-placeholder-blue'
                  }`}></div>
              )}
               {getCategoryImage(category) && (
                <>
                  {/* Subtle tinting overlay for quality improvement - not too concentrated */}
                  <div className="absolute inset-0 bg-gradient-to-b from-primary-900/30 via-primary-800/20 to-primary-900/40"></div>
                  {/* Enhanced overlay for text readability */}
                  <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/50"></div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
          {/* Category Badge */}
          <Link
            to={isDefault ? "/tours" : `/category/${currentCategory.id}`}
            className={`cursor-pointer z-30 pointer-events-auto hover:scale-105 transition-all duration-300 hidden sm:inline-flex items-center backdrop-blur-md px-6 py-2 rounded-full font-medium text-sm mb-6 sm:mb-8 animate-fade-in border shadow-lg ${actualTheme === 'dark'
              ? 'bg-white/15 hover:bg-white/25 text-white border-white/30 hover:border-white/50'
              : 'bg-white/20 hover:bg-white/30 text-slate-800 border-white/40 hover:border-white/60'
              }`}
            style={{
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.5)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <StarIcon className="h-4 w-4 mr-2" />
            <span>{isDefault ? 'Premier East African Expeditions' : currentCategory.name}</span>
          </Link>

          {/* Category Name as Title */}
          <h1
            className={`text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold mb-2 sm:mb-3 md:mb-4 animate-slide-up leading-tight px-2 ${
              getCategoryImage(currentCategory) ? 'text-white' : (actualTheme === 'dark' ? 'text-white' : 'text-primary-900')
            }`}
            style={{
              textShadow: getCategoryImage(currentCategory) 
                ? '0 4px 20px rgba(0, 0, 0, 0.8), 0 2px 10px rgba(0, 0, 0, 0.6), 0 0 40px rgba(0, 0, 0, 0.4)'
                : 'none'
            }}
          >
            {currentCategory.name}
          </h1>

          {/* Description */}
          <div
            className={`text-xs sm:text-sm md:text-base lg:text-lg mb-4 sm:mb-5 animate-slide-up delay-100 max-w-2xl mx-auto leading-relaxed px-2 sm:px-0 ${
              getCategoryImage(currentCategory) ? 'text-white/95' : (actualTheme === 'dark' ? 'text-slate-300' : 'text-slate-600')
            }`}
            style={{
              textShadow: getCategoryImage(currentCategory)
                ? '0 2px 12px rgba(0, 0, 0, 0.7), 0 1px 6px rgba(0, 0, 0, 0.5)'
                : 'none'
            }}
          >
            <p className="line-clamp-2 sm:line-clamp-3 overflow-hidden text-ellipsis px-1">{currentCategory.description || getCategorySubtitle(currentCategory.name)}</p>
          </div>

          {/* Action Buttons - Responsive Layout */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center animate-slide-up delay-300 px-2 mt-8">
            <Link
              to={isDefault ? "/tours" : `/category/${currentCategory.id}`}
              className="w-auto max-w-fit bg-gradient-to-r from-primary-600 to-primary-800 hover:from-primary-500 hover:to-primary-700 text-white px-6 sm:px-10 py-3 sm:py-4 rounded-full font-semibold text-sm sm:text-base transition-all duration-300 shadow-[0_8px_30px_rgb(0,0,0,0.3)] hover:shadow-[0_8px_40px_rgba(var(--primary-600),0.4)] hover:-translate-y-1 flex items-center justify-center min-h-[48px] sm:min-w-[220px] cursor-pointer z-30 relative pointer-events-auto touch-manipulation border border-primary-400/20"
              style={{
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
              }}
              onClick={(e) => {
                if (isDefault) return; // Navigate to tours

                // Ensure navigation works for categories
                if (!currentCategory?.id) {
                  e.preventDefault();
                  console.warn('Category ID not available');
                  return;
                }
                // Explicitly navigate
                e.stopPropagation();
              }}
            >
              {isDefault ? 'Start Your Journey' : `Explore ${currentCategory.name}`}
            </Link>

            <Link
              to="/tours"
              className="w-auto max-w-fit bg-white/10 backdrop-blur-xl hover:bg-white/20 text-white px-5 sm:px-8 py-3 sm:py-4 rounded-full font-semibold text-sm sm:text-base flex items-center justify-center space-x-2 min-h-[48px] cursor-pointer z-30 relative pointer-events-auto touch-manipulation transition-all duration-300 shadow-[0_8px_30px_rgb(0,0,0,0.2)] hover:shadow-[0_8px_40px_rgb(255,255,255,0.15)] border border-white/20 hover:border-white/40 hover:-translate-y-1"
              style={{
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <MapPinIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white/90" />
              <span>All Tours</span>
            </Link>
          </div>
        </div>

      </div>
    </section>
  );
};

export default CategoryHeroSlideshow;
